"use client";
import useFirebaseStore from "@/store/firebase";
import {
  useReplicachePreferences,
  useReplicacheStore,
} from "@/store/replicache";
import chordProToSong from "@/utils/chordProToSong";
import clearLocalData from "@/utils/clearLocalData";
import { NEW_SONG_PLACEHOLDER } from "@/utils/constants";
import findSongs from "@/utils/findSongs";
import { getSongEditorRoute, getSongViewRoute } from "@/utils/getSongRoute";
import isChordProFileName from "@/utils/isChordProFileName";
import readFileAsText from "@/utils/readFileAsText";
import storage from "@/utils/storage";
import type {
  SetList,
  Song,
  SongId,
  Songs,
  SortKey,
  TabValue,
  User,
  UserId,
  UserPreferences,
} from "@/utils/types";
import { format } from "date-fns";
import { isNil } from "lodash";
import { signIn, signOut } from "next-auth/react";
import { useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { useDebouncedCallback } from "use-debounce";
import create from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import shallow from "zustand/shallow";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type SongModifyArg = { songId: SongId; update: any };

const LOG_TAG = "🏪 store/app";
const DEBUG = false;

export type AppContextProps = {
  initializedState: boolean;
  setInitializedState: (T: boolean) => void;
  initializedStateFromStorage: boolean;
  setInitializedStateFromStorage: (T: boolean) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (T: boolean) => void;
  selectedTab: TabValue;
  setSelectedTab: (T: TabValue) => void;
  includeFriendsSongs: boolean;
  setIncludeFriendsSongs: (T: boolean) => void;
  activeChord: string;
  setActiveChord: (T: string) => void;
  searchKey: string;
  setSearchKey: (T: string) => void;
  sortKey: SortKey;
  setSortKey: (T: SortKey) => void;
  user: User | undefined;
  setUser: (T: User) => void;
  welcomeStep: number;
  setWelcomeStep: (T: number) => void;
  isAutoPlaying: boolean;
  setIsAutoPlaying: (T: boolean) => void;
  theme: string;
  setTheme: (T: string) => void;

  songs: Songs;

  songsImporting: boolean;
  setSongsImporting: (T: boolean) => void;
  waitingOnAuth: boolean;
  setWaitingOnAuth: (T: boolean) => void;
  windowSize: { width: number; height: number };
  setWindowSize: (T: { width: number; height: number }) => void;
  onLine: boolean;
  setOnLine: (T: boolean) => void;
};

export const useAppStore = create<AppContextProps>(
  // @ts-ignore
  subscribeWithSelector((set, _) => ({
    initializedState: false,
    setInitializedState: (initializedState: boolean) =>
      set({ initializedState }),
    initializedStateFromStorage: false,
    setInitializedStateFromStorage: (initializedStateFromStorage: boolean) =>
      set({ initializedStateFromStorage }),
    isSearchOpen: false,
    setIsSearchOpen: (isSearchOpen: boolean) => set({ isSearchOpen }),
    selectedTab: "songlist",
    setSelectedTab: (selectedTab: TabValue) => set({ selectedTab }),
    includeFriendsSongs: false,
    setIncludeFriendsSongs: (includeFriendsSongs: boolean) =>
      set({ includeFriendsSongs }),
    activeChord: "",
    setActiveChord: (activeChord: string) => set({ activeChord }),
    searchKey: "",
    setSearchKey: (searchKey: string) => set({ searchKey }),
    sortKey: "title",
    setSortKey: (sortKey: SortKey) => set({ sortKey }),
    user: undefined,
    setUser: (user: User) => set({ user }),
    welcomeStep: -1,
    setWelcomeStep: (welcomeStep: number) => set({ welcomeStep }),
    isAutoPlaying: false,
    setIsAutoPlaying: (isAutoPlaying: boolean) => set({ isAutoPlaying }),
    theme: "dark",
    setTheme: (theme: string) => set({ theme }),

    songs: {},

    songsImporting: false,
    setSongsImporting: (songsImporting: boolean) => set({ songsImporting }),
    waitingOnAuth: true,
    setWaitingOnAuth: (waitingOnAuth: boolean) => set({ waitingOnAuth }),
    windowSize: { width: 800, height: 800 },
    setWindowSize: (windowSize: { width: number; height: number }) =>
      set({ windowSize }),
    onLine: true,
    setOnLine: (onLine: boolean) => set({ onLine }),
  })),
);

function clientOnlyCallBack<T>(cb: T): T {
  if (typeof window !== "undefined") {
    return cb;
  }
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return (() => {}) as any;
}

useAppStore.subscribe(
  // @ts-ignore
  (state) => state.selectedTab,
  // @ts-ignore
  clientOnlyCallBack((selectedTab) => {
    storage.setItem("selectedTab", selectedTab);
  }),
);

useAppStore.subscribe(
  // @ts-ignore
  (state) => state.sortKey,
  // @ts-ignore
  clientOnlyCallBack((sortKey) => {
    if (DEBUG) console.debug(LOG_TAG, "storage.setItem()", { sortKey });
    storage.setItem("sortKey", sortKey);
  }),
);

useAppStore.subscribe(
  // @ts-ignore
  (state) => state.includeFriendsSongs,
  // @ts-ignore
  clientOnlyCallBack((includeFriendsSongs) => {
    if (DEBUG) console.debug(LOG_TAG, "storage.setItem()", { includeFriendsSongs });
    storage.setItem("includeFriendsSongs", includeFriendsSongs);
  }),
);

useAppStore.subscribe(
  // @ts-ignore
  (state) => state.theme,
  // @ts-ignore
  clientOnlyCallBack((theme) => {
    if (DEBUG) console.debug(LOG_TAG, "storage.setItem()", { theme });
    storage.setItem("theme", theme);
  }),
);

useAppStore.subscribe(
  ({
    setUser,
    setSelectedTab,
    setSortKey,
    setIncludeFriendsSongs,
    initializedStateFromStorage,
    setInitializedStateFromStorage,
    setTheme,
  }) => ({
    setUser,
    setSelectedTab,
    setSortKey,
    setIncludeFriendsSongs,
    initializedStateFromStorage,
    setInitializedStateFromStorage,
    setTheme,
  }),
  // @ts-ignore
  ({
    setUser,
    setSelectedTab,
    setSortKey,
    setIncludeFriendsSongs,
    initializedStateFromStorage,
    setInitializedStateFromStorage,
    setTheme,
  }) => {
    if (typeof window === "undefined") {
      return;
    }
    // Maps field to setter.
    const LOCAL_STORAGE_MAP: Record<
      string,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      (value: any) => void
    > = {
      selectedTab: setSelectedTab,
      sortKey: setSortKey,
      includeFriendsSongs: setIncludeFriendsSongs,
      user: setUser,
      theme: setTheme,
    };

    const setStateFromStorage = async () => {
      const localStorage: Record<string, unknown> = {};
      for (const field of Object.keys(LOCAL_STORAGE_MAP)) {
        const localValue = await storage.getItem(field);
        /*console.debug(LOG_TAG, ".. looking in storage for field", field, "got", {
          [field]: { ...localValue },
        });*/
        if (!isNil(localValue)) {
          localStorage[field] = localValue;
        }
      }

      for (const field of Object.keys(localStorage)) {
        /*
        console.debug(LOG_TAG, "setStateFromStorage ${field} ->", {
          [`${field}`]: localStorage[field],
        });
        */
        LOCAL_STORAGE_MAP[field](localStorage[field]);
      }
    };
    if (initializedStateFromStorage) {
      return;
    }
    const fn = async () => {
      await setStateFromStorage();
      setInitializedStateFromStorage(true);
    };
    fn();
  },
  shallow,
);

// This should only return an object with functions, never data, that should come from the app store.
export function useApp() {
  const router = useRouter();
  const firebaseCtx = useFirebaseStore(
    ({
      user,
      setListAddGetKey,
      songAddGetKey,
      userModify,
      fileUpload,
      signOut,
      userProfileGet,
    }) => ({
      user,
      setListAddGetKey,
      songAddGetKey,
      userModify,
      fileUpload,
      signOut,
      userProfileGet,
    }),
    shallow,
  );
  const replicacheStore = useReplicacheStore(
    (state) => ({
      setPreferences: state.setPreferences,
      songViewCountIncrement: state.songViewCountIncrement,
      setListAdd: state.setListAdd,
      setListModify: state.setListModify,
      setListRemove: state.setListRemove,
      songAdd: state.songAdd,
      songModify: state.songModify,
      songRemove: state.songRemove,
    }),
    shallow,
  );

  const { songs, user } = useAppStore(
    ({ songs, user }) => ({ songs, user }),
    shallow,
  );

  const { preferences } = useReplicachePreferences();

  const setPreferences = useDebouncedCallback(
    (newPreferences: UserPreferences) => {
      replicacheStore.setPreferences(newPreferences);
    },
    500,
  );

  const getSongById = (id: string) => {
    return songs[id];
  };

  const _songAdd = async (song: Song): Promise<Song> => {
    if (!user?.id) {
      throw new Error("Can't call _songAdd without user");
    }
    song.id = firebaseCtx.songAddGetKey();

    const ts = Date.now();
    const songDefaults = {
      view_count: 0,
      created_timestamp: ts,
      updated_timestamp: ts,
      deleted_timestamp: 0,
      created_by_user_id: user.id,
      updated_by_user_id: user.id,
    };

    // biome-ignore lint/style/noParameterAssign: <explanation>
    song = {
      ...songDefaults,
      ...song,
    };

    if (DEBUG) console.debug(LOG_TAG, "songAdd", { song });
    await replicacheStore.songAdd({ song, ts });
    return song;
  };

  const songAddEmpty = async () => {
    if (!user?.id) {
      throw new Error("Can't call songAddEmpty without user");
    }
    const ts = Date.now();
    const song = {
      id: "",
      title: "New Song",
      artist: preferences?.default_artist || "",
      duration: 0,
      capo: 0,
      view_count: 0,
      key: "",
      chordPro: NEW_SONG_PLACEHOLDER,
      audioFileUrl: null, // Change this from "" to null
      created_timestamp: ts,
      updated_timestamp: ts,
      deleted_timestamp: 0,
      created_by_user_id: user.id,
      updated_by_user_id: user.id,
    };
    return await _songAdd(song);
  };

  const songModifyReplicache = useDebouncedCallback(async (song: Song) => {
    //console.debug(LOG_TAG, "songModifyReplicache", song);
    await replicacheStore.songModify(song);
  }, 500);

  const _songModify = ({ songId, update }: SongModifyArg): Song => {
    if (!user?.id) {
      throw new Error("Can't call songModify without user");
    }
    const prevSong = songs[songId];
    if (!prevSong) {
      throw new Error("song state mismatch in songModify");
    }
    /*
    console.debug(LOG_TAG, "songModify", {
      song,
      songId,
      update,
    });
    */

    const song = { ...prevSong, ...update };
    songModifyReplicache.callback(song);
    return song;
  };

  const _songRemove = (song: Song) => {
    const matchingSongs = findSongs({
      songs,
      title: song.title,
      artist: song.artist,
    });
    if (DEBUG) console.debug(LOG_TAG, "songRemove", { song, matchingSongs });
    const ts = Date.now();
    for (const song of matchingSongs) {
      replicacheStore.songRemove({ song, ts });
    }
  };

  const _setListAdd = async (
    setListArg: Partial<SetList>,
  ): Promise<SetList> => {
    if (!user?.id) {
      throw new Error("Can't call _setListAdd without user");
    }

    const ts = Date.now();
    const setListDefaults = {
      title: "New Set List",
      songIds: [],
      added_timestamp: ts,
      created_timestamp: ts,
      updated_timestamp: ts,
      deleted_timestamp: 0,
      created_by_user_id: user.id,
      updated_by_user_id: user.id,
    };

    const setList: SetList = {
      ...setListDefaults,
      ...setListArg,
      id: firebaseCtx.setListAddGetKey(),
    };

    if (DEBUG) console.debug(LOG_TAG, "setListAdd", { setList });
    await replicacheStore.setListAdd({ setList, ts });
    return setList;
  };

  const _setListModify = (setList: SetList): SetList => {
    if (!user?.id) {
      throw new Error("Can't call _setListModify without user");
    }

    if (DEBUG) console.debug(LOG_TAG, "setListModify", { setList });
    const ts = Date.now();
    replicacheStore.setListModify({ setList, ts });
    return setList;
  };

  const _setListRemove = ({ setList }: { setList: SetList }): SetList => {
    if (!user?.id) {
      throw new Error("Can't call _setListRemove without user");
    }
    if (setList.created_by_user_id !== user.id) {
      throw new Error("Can't call setListRemove unless created by user");
    }

    if (DEBUG) console.debug(LOG_TAG, "_setListRemove", { setList });
    const ts = Date.now();
    replicacheStore.setListRemove({ setList, ts });
    return setList;
  };

  const fileUpload = async (file: File): Promise<string> => {
    if (!user) return "";
    const downloadurl = await firebaseCtx.fileUpload(file, user.id);
    return downloadurl;
  };

  const userProfileGet = async (userId: UserId) => {
    const user = await firebaseCtx.userProfileGet(userId);
    return user;
  };

  const _signIn = async () => {
    if (DEBUG) console.debug(LOG_TAG, "signIn");
    try {
      await signIn("google");
      await clearLocalData();
    } catch (err) {
      console.error(LOG_TAG, "signIn failed", err);
    }
  };

  const firebaseSignOut = firebaseCtx.signOut;
  const _signOut = async () => {
    if (DEBUG) console.debug(LOG_TAG, "signOut");
    try {
      await firebaseSignOut();
      await signOut();
      await clearLocalData();
      router.push("/");
    } catch (err) {
      console.error(LOG_TAG, "signOut failed", err);
    }
  };

  const routeToViewSong = (song: Song) => {
    const href = getSongViewRoute({ song });
    if (DEBUG) console.debug(`${LOG_TAG} routeToViewSong`, { href, song });
    router.push(href);
  };

  return {
    fileUpload,
    getSongById,
    signIn: _signIn,
    signOut: _signOut,

    setPreferences,

    songAdd: _songAdd,
    songAddEmpty,
    songModify: _songModify,
    songRemove: _songRemove,

    setListAdd: _setListAdd,
    setListModify: _setListModify,
    setListRemove: _setListRemove,

    userProfileGet,

    routeToViewSong,
    songViewCountIncrement: replicacheStore.songViewCountIncrement,
  };
}

export function useHandleClickCreateNewSong() {
  const { user } = useAppStore(({ user }) => ({ user }), shallow);
  const router = useRouter();
  const { songAdd, songAddEmpty } = useApp();

  const handleClickCreateNewSong = async (_e: MouseEvent, songData?: Song) => {
    if (!user) {
      router.push("/signin");
      return;
    }
    let song: Song;
    let href: string;
    if (!songData) {
      song = await songAddEmpty();
      href = getSongEditorRoute({ song });
    } else {
      song = await songAdd(songData);
      href = getSongViewRoute({ song });
    }
    if (DEBUG) console.debug(`${LOG_TAG} handleClickCreateNewSong`, {
      href,
      song,
    });
    router.push(href);
  };
  return handleClickCreateNewSong;
}

export function useHandleClickCreateNewSetList() {
  const user = useAppStore(({ user }) => user);
  const { setListAdd, signIn } = useApp();
  const handleClickCreateNewSetList = async (_e: MouseEvent) => {
    if (!user) {
      signIn();
      return;
    }
    if (DEBUG) console.debug(LOG_TAG, "yoyoyoyo");
    const title = window.prompt(
      "Set Name Yo",
      `Set-${format(new Date(), "MM-dd-h:mmaaa")}`,
    );
    if (!title) return;
    await setListAdd({ title });
  };
  return handleClickCreateNewSetList;
}

export function useAppFile() {
  const songs = useReplicacheStore(({ songs }) => songs);
  const user = useAppStore(({ user }) => user);
  const { fileUpload, songAdd } = useApp();

  const setSongsImporting = useAppStore(
    ({ setSongsImporting }) => setSongsImporting,
  );

  const importPdfFile = async (file: File) => {
    if (DEBUG) console.debug(LOG_TAG, "importPdfFile", { file });
    if (!user?.id) throw new Error("Can't import pdf without user");
    try {
      const downloadUrl = await fileUpload(file);
      if (DEBUG) console.debug(LOG_TAG, "importPdfFile", { downloadUrl });
      const song: Song = {
        id: "",
        title: file.name.replace(".pdf", ""),
        artist: "",
        capo: 0,
        duration: 0,
        key: "",
        chordPro: `{x_pdf: src="${downloadUrl}"}`,
        audioFileUrl: "",
        created_timestamp: Date.now(),
        updated_timestamp: 0,
        deleted_timestamp: 0,
        created_by_user_id: user?.id,
        updated_by_user_id: "",
      };
      songAdd(song);
    } catch (error) {
      console.error(LOG_TAG, "importPdfFile error", { file, error });
    }
  };

  const importChordProFile = async (file: File) => {
    if (!user) throw new Error("Can't import chordpro without user");
    try {
      const chordPro = (await readFileAsText(file)) as string;
      const song = chordProToSong({ chordPro, id: "", userId: user.id });
      if (DEBUG) console.debug(LOG_TAG, "importChordProFile chordProToSong", {
        chordPro,
        song,
      });
      const existingSongs = findSongs({
        songs,
        title: song.title,
        artist: song.artist,
      });
      // idempotent on title/artist as key
      if (existingSongs.length > 0) {
        console.warn(LOG_TAG, "importChordProFile bail", {
          file,
          existingSongs,
        });
      } else {
        songAdd(song);
      }
    } catch (error) {
      console.error(LOG_TAG, "importChordProFile error", { file, error });
    }
  };

  const handleDrop = async (acceptedFiles: File[]) => {
    if (DEBUG) console.debug(LOG_TAG, "handleDrop!", acceptedFiles);
    if (!user) {
      console.warn(LOG_TAG, "Can't add songs without a user");
      return;
    }
    setSongsImporting(true);

    for (const file of acceptedFiles) {
      if (isChordProFileName(file.name)) {
        await importChordProFile(file);
      } else if (file.name.endsWith(".pdf")) {
        await importPdfFile(file);
      } else {
        if (DEBUG) console.debug(LOG_TAG, "handleDrop ignoring non-handled file", {
          file,
        });
      }
    }
    setSongsImporting(false);
  };

  return {
    importChordProFile,
    importPdfFile,
    handleDrop,
  };
}
